/**
 * Share of voice per signal, as three pies — in a scope of the reader's
 * choosing (2026-09-08).
 *
 * Requested by Lisa (2026-09-01: "I'm wondering if a pie chart would be better
 * here") and by Ramos directly. A pie is legitimate here for the reason it
 * usually is not: each mention, citation or recommendation is one
 * (answer, company) event belonging to exactly ONE company, so the slices
 * genuinely compose to a whole.
 *
 * THREE SCOPES (Lisa + Ramos, 2026-09-08). The default divides EVERY company
 * AI named — 241 on the Antibodies Inc run — which is the honest denominator
 * and shows a 70% "Other". Lisa's first question was "who's everyone else?",
 * Ramos asked for the inverse ("only the top tier ... Thermo would be the big
 * slice"), and Lisa for a third: "focus it on their specific competitors."
 * All three are one control above the pies; each states its own denominator.
 *
 * ONE COLOUR PER COMPANY. Lisa: "consistent in colour, because if we do a deck
 * we'll be using the same" — she had just watched Thermo be orange in one pie
 * and green in the next. `signalShares` now fixes ONE head of companies in ONE
 * order across all three pies, so the same company sits in the same position
 * with the same colour in each, and the change in a wedge's size between pies
 * is the story. The client takes categorical slot 1 (its accent everywhere in
 * this report) and always the first wedge from twelve o'clock, so its sliver
 * is in the same place in every pie; it is exploded and labelled with a leader
 * line. Slices wide enough to hold a number are labelled directly.
 *
 * Scopes draw disjoint populations (the leaders never appear in the
 * named-set pie and vice versa), so a hue reused across scopes never puts two
 * companies of one colour on screen together; only the client persists, and it
 * keeps its accent. "Other" takes context grey, because a tail is not a series.
 */
import {
  signalShareLeader,
  type SignalShare,
  type SignalShareScope,
  type SignalShareScopeOption,
  type SignalShareSlice,
} from '@aurora/shared';
import { CATEGORICAL, CONTEXT, TARGET } from './palette';

const SIZE = 250;
const R_OUT = 90;
const CX = SIZE / 2;
const CY = SIZE / 2;
const EXPLODE = 9;
/** Below this a wedge cannot hold a label; the legend carries it. */
const LABEL_MIN = 0.07;

const pct = (v: number): string => {
  const p = v * 100;
  if (p === 0) return '0%';
  if (p > 0 && p < 1) return '<1%';
  if (p < 10) return `${p.toFixed(1)}%`;
  return `${Math.round(p)}%`;
};

function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

function wedge(startDeg: number, endDeg: number, cx: number, cy: number): string {
  if (endDeg - startDeg >= 359.9) {
    // One slice holding the whole — a wedge path degenerates, so draw the disc.
    return `M ${cx - R_OUT} ${cy} a ${R_OUT} ${R_OUT} 0 1 0 ${R_OUT * 2} 0 a ${R_OUT} ${R_OUT} 0 1 0 ${-R_OUT * 2} 0 Z`;
  }
  const large = endDeg - startDeg > 180 ? 1 : 0;
  const [x1, y1] = polar(cx, cy, R_OUT, startDeg);
  const [x2, y2] = polar(cx, cy, R_OUT, endDeg);
  return `M ${cx} ${cy} L ${x1} ${y1} A ${R_OUT} ${R_OUT} 0 ${large} 1 ${x2} ${y2} Z`;
}

function hueFor(s: SignalShareSlice, headIndex: number): string {
  if (s.isTarget) return TARGET;
  if (s.isOther) return CONTEXT;
  // Slots 2..6 in the head's fixed order — slot 1 is reserved for the client.
  return CATEGORICAL[Math.min(headIndex + 1, CATEGORICAL.length - 1)] as string;
}

type YouLabel = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  tx: number;
  anchor: 'start' | 'end';
};

function Pie({ share }: { share: SignalShare }): JSX.Element | null {
  if (share.slices.length === 0) return null;

  let cursor = 0;
  let headIndex = 0;
  const arcs = share.slices.map((s) => {
    const sweep = s.share * 360;
    const start = cursor;
    cursor += sweep;
    const colour = hueFor(s, headIndex);
    if (!s.isTarget && !s.isOther) headIndex += 1;
    const mid = start + sweep / 2;
    // Nudge the client's wedge out of the pie so a 1% sliver is findable.
    const [ox, oy] = s.isTarget
      ? polar(0, 0, EXPLODE, mid)
      : ([0, 0] as [number, number]);
    return {
      slice: s,
      colour,
      sweep,
      mid,
      d: sweep > 0 ? wedge(start, start + sweep, CX + ox, CY + oy) : null,
    };
  });

  const you = arcs.find((a) => a.slice.isTarget) ?? null;
  let youLabel: YouLabel | null = null;
  if (you && you.sweep > 0) {
    const [x1, y1] = polar(CX, CY, R_OUT + EXPLODE + 2, you.mid);
    const [x2, y2] = polar(CX, CY, R_OUT + 20, you.mid);
    const right = Math.cos(((you.mid - 90) * Math.PI) / 180) >= 0;
    youLabel = {
      x1,
      y1,
      x2,
      y2,
      tx: x2 + (right ? 4 : -4),
      anchor: right ? 'start' : 'end',
    };
  }

  return (
    <figure className="signal-pie">
      <figcaption className="signal-pie-title">{share.label}</figcaption>
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={`${share.label}: share of voice across ${share.companies} companies`}
        style={{ overflow: 'visible' }}
      >
        {arcs
          .filter((a) => a.d !== null)
          .map((a) => (
            <path
              key={a.slice.company}
              d={a.d ?? ''}
              fill={a.colour}
              stroke="#0b1020"
              strokeWidth={1.5}
            >
              <title>
                {a.slice.company}: {a.slice.value} of {share.total} (
                {pct(a.slice.share)})
              </title>
            </path>
          ))}
        {/* Direct labels — only on wedges wide enough to hold one. */}
        {arcs
          .filter((a) => !a.slice.isTarget && a.slice.share >= LABEL_MIN)
          .map((a) => {
            const [lx, ly] = polar(CX, CY, R_OUT * 0.62, a.mid);
            return (
              <text
                key={`l-${a.slice.company}`}
                x={lx}
                y={ly}
                textAnchor="middle"
                dominantBaseline="middle"
                className="pie-slice-label"
              >
                {pct(a.slice.share)}
              </text>
            );
          })}
        {you && youLabel && (
          <>
            <polyline
              points={`${youLabel.x1},${youLabel.y1} ${youLabel.x2},${youLabel.y2}`}
              className="pie-you-line"
            />
            <text
              x={youLabel.tx}
              y={youLabel.y2}
              textAnchor={youLabel.anchor}
              dominantBaseline="middle"
              className="pie-you-label"
            >
              You · {pct(you.slice.share)}
            </text>
          </>
        )}
      </svg>
      {share.target && (
        <p className="signal-pie-callout">
          You: <strong>{pct(share.target.share)}</strong> ({share.target.value}{' '}
          of {share.total})
        </p>
      )}
      <ul className="pie-legend">
        {arcs.map((a) => (
          <li
            key={a.slice.company}
            className={a.slice.value === 0 ? 'pie-legend-zero' : undefined}
          >
            <span
              className="chart-legend-swatch"
              style={{ background: a.colour }}
            />
            <span
              className={
                a.slice.isTarget ? 'pie-legend-label is-target' : 'pie-legend-label'
              }
            >
              {a.slice.isTarget ? `${a.slice.company} (you)` : a.slice.company}
            </span>
            <span className="pie-legend-pct">{pct(a.slice.share)}</span>
          </li>
        ))}
      </ul>
    </figure>
  );
}

function Caption({ shares }: { shares: SignalShare[] }): JSX.Element | null {
  const mentions = shares.find((s) => s.key === 'mention') ?? shares[0];
  if (!mentions) return null;
  const you = mentions.target;
  const leader = signalShareLeader(mentions);
  // The biggest company that is not the client — the comparison the
  // scoped captions draw.
  const lead = leader?.rival ?? null;
  const youLead = leader?.youLead ?? false;
  const other = mentions.slices.find((s) => s.isOther) ?? null;
  const headCount = mentions.slices.filter(
    (s) => !s.isTarget && !s.isOther,
  ).length;

  if (mentions.scope === 'named') {
    const rivals = mentions.slices.filter((s) => !s.isTarget && !s.isOther);
    const silent = rivals.filter((s) => s.value === 0);
    return (
      <p className="chart-verdict">
        Shares within <strong>your named competitive set only</strong> — you and
        the {rivals.length} {rivals.length === 1 ? 'competitor' : 'competitors'}{' '}
        you listed — not the whole conversation.
        {you && (
          <>
            {' '}
            Within that set you {youLead ? 'lead with' : 'hold'}{' '}
            <strong>{pct(you.share)}</strong> of {mentions.label.toLowerCase()}
            {lead ? ` against ${lead.company}'s ${pct(lead.share)}` : ''}.
          </>
        )}
        {silent.length > 0 && (
          <>
            {' '}
            AI never named {silent.map((s) => s.company).join(', ')} in this
            benchmark.
          </>
        )}
      </p>
    );
  }
  if (mentions.scope === 'leaders') {
    return (
      <p className="chart-verdict">
        Shares within <strong>these {headCount + 1} companies only</strong> —
        the {headCount} that rank highest across mentions, citations and
        recommendations in this benchmark, plus you — not the whole
        conversation.
        {you && lead && (
          <>
            {' '}
            Against the leaders alone you {youLead ? 'lead with' : 'hold'}{' '}
            <strong>{pct(you.share)}</strong> of {mentions.label.toLowerCase()};{' '}
            {lead.company} holds {pct(lead.share)}.
          </>
        )}
      </p>
    );
  }
  return (
    <p className="chart-verdict">
      These divide up every time AI named, cited or recommended <em>any</em>{' '}
      supplier — {mentions.total} mentions across{' '}
      <strong>{mentions.companies} different companies</strong> in this
      benchmark, not just the ones in the table above.
      {leader && <AllScopeLeader leader={leader} />}
      {other && other.companies !== undefined && (
        <>
          {' '}
          &ldquo;Other&rdquo; is the {other.companies} companies outside the top{' '}
          {headCount} and you — none holding more than {pct(mentions.otherMax)}.
        </>
      )}
    </p>
  );
}

/**
 * Who is most-named across everyone AI named. The client is a candidate like
 * any other company: when it holds the biggest wedge, the caption says so.
 */
function AllScopeLeader({
  leader,
}: {
  leader: NonNullable<ReturnType<typeof signalShareLeader>>;
}): JSX.Element {
  const { top, next, youLead, tied } = leader;
  const name = (s: SignalShareSlice): string => (s.isTarget ? 'you' : s.company);
  if (youLead) {
    return (
      <>
        {' '}
        You are the most-named company, at <strong>{pct(top.share)}</strong> of
        the conversation
        {next ? `; the next, ${next.company}, holds ${pct(next.share)}` : ''}.
      </>
    );
  }
  if (tied && next) {
    const [a, b] = top.isTarget || !next.isTarget ? [top, next] : [next, top];
    return (
      <>
        {' '}
        The most-named companies, <strong>{name(a)}</strong> and{' '}
        <strong>{name(b)}</strong>, hold {pct(top.share)} of the conversation
        each.
      </>
    );
  }
  // "Owns" is only denied when no one holds even a quarter of the conversation.
  const owns = top.share < 0.25 ? 'No one owns this category: the' : 'The';
  return (
    <>
      {' '}
      {owns} most-named company, <strong>{top.company}</strong>, holds
      {top.share < 0.25 ? ' only' : ''} {pct(top.share)} of the conversation.
    </>
  );
}

export function SignalSharePies({
  shares,
  scopes,
  scope,
  onScopeChange,
}: {
  shares: SignalShare[];
  scopes: SignalShareScopeOption[];
  scope: SignalShareScope;
  onScopeChange: (scope: SignalShareScope) => void;
}): JSX.Element | null {
  if (shares.length === 0 && scopes.length === 0) return null;

  return (
    <div className="signal-pies-wrap">
      {scopes.length > 1 && (
        <div className="funnel-controls">
          <label className="sov-filter">
            <span className="muted">Share of</span>
            <select
              value={scope}
              onChange={(e) => onScopeChange(e.target.value as SignalShareScope)}
            >
              {scopes.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      {shares.length > 0 ? (
        <>
          <div className="signal-pies">
            {shares.map((s) => (
              <Pie key={s.key} share={s} />
            ))}
          </div>
          <Caption shares={shares} />
        </>
      ) : (
        <p className="muted">Nothing to divide up in this scope.</p>
      )}
    </div>
  );
}
